import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PermanentAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface EmploymentStatus {
  employmentStatus: string;
  employerName: string;
  companyName: string;
  jobTitle: string;
  incomeSource: string;
  companyAddress?: string;
  lineOfBusiness?: string;
  unemploymentSource?: string;
}

export interface RegistrationData {
  currentStep: string;
  miniRegistrationStep: string;
  data: {
    accountType: string;
    currentStep?: string;
    dateOfBirth: string;
    email: string;
    employmentStatus: EmploymentStatus;
    essentialServiceCommunications: boolean;
    firstName: string;
    holdHarmlessConsent: boolean;
    identityVerified: boolean;
    isBusinessAccount: boolean;
    lastName: string;
    miniRegistrationStep: string;
    otherPurpose?: string;
    otherSourceOfFunds?: string;
    permanentAddress: PermanentAddress;
    phoneNumber: string;
    privacyAndDataAccepted: boolean;
    purpose: string;
    salaryRange: string;
    sourceOfFunds: string;
    ssn: string;
    summaryConfirmed: boolean;
    termsAccepted: boolean;
    zohoCustomerId: string;
    sumsubApplicantId: string;
    anonymousId?: string; // Store the anonymous ID for tracking
  };
}

interface RegistrationStore {
  currentStep: string;
  fullRegistrationStep: number;
  isPrivateMode: boolean;
  data: RegistrationData['data'];
  updateData: (newData: Partial<RegistrationData['data']>) => void;
  setCurrentStep: (step: string) => void;
  setFullRegistrationStep: (step: number) => void;
  clearData: () => void;
  setPrivateMode: (isPrivate: boolean) => void;
  generateZohoCustomerId: () => string;
  initializeWithAnonymousId: () => void;
  logout: () => void;
}

const initialData: RegistrationData['data'] = {
  accountType: '',
  dateOfBirth: '',
  email: '',
  employmentStatus: {
    employmentStatus: '',
    employerName: '',
    companyName: '',
    jobTitle: '',
    incomeSource: '',
  },
  essentialServiceCommunications: false,
  firstName: '',
  holdHarmlessConsent: false,
  identityVerified: false,
  isBusinessAccount: false,
  lastName: '',
  miniRegistrationStep: '1',
  permanentAddress: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  phoneNumber: '',
  privacyAndDataAccepted: false,
  purpose: '',
  salaryRange: '',
  sourceOfFunds: '',
  ssn: '',
  summaryConfirmed: false,
  termsAccepted: false,
  zohoCustomerId: '',
  sumsubApplicantId: '',
  anonymousId: '',
};

export const useRegistrationStore = create<RegistrationStore>()(
  persist(
    (set) => ({
      currentStep: 'personal-info',
      fullRegistrationStep: 1,
      isPrivateMode: true,
      data: initialData,

      updateData: (newData) =>
        set((state) => ({
          data: { ...state.data, ...newData },
        })),

      setCurrentStep: (step) =>
        set(() => ({
          currentStep: step,
        })),

      setFullRegistrationStep: (step) =>
        set(() => ({
          fullRegistrationStep: step,
        })),

      setPrivateMode: (isPrivate) =>
        set(() => ({
          isPrivateMode: isPrivate,
        })),

      clearData: () =>
        set(() => ({
          currentStep: 'personal-info',
          fullRegistrationStep: 1,
          data: initialData,
        })),

      initializeWithAnonymousId: () => {
        const anonymousId = localStorage.getItem('anonymousId');
        if (anonymousId) {
          set((state) => ({
            data: { ...state.data, anonymousId },
          }));
        }
      },

      generateZohoCustomerId: () => {
        const timestamp = Date.now().toString();
        const random = Math.floor(Math.random() * 10000)
          .toString()
          .padStart(4, '0');
        const newId = `6${timestamp}${random}`;
        const finalId = newId.slice(0, 20).padEnd(20, '0');

        set((state) => ({
          data: { ...state.data, zohoCustomerId: finalId },
        }));

        return finalId;
      },

      logout: () => {
        set(() => ({
          currentStep: 'personal-info',
          fullRegistrationStep: 1,
          data: initialData,
        }));
      },
    }),
    {
      name: 'registration-storage',
      partialize: (state) => (state.isPrivateMode ? state : { isPrivateMode: false }),
    },
  ),
);
